import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, ListPlusIcon, PlusIcon } from 'lucide-react';
import type { Track } from '../types/music';
import { useLibrary } from '../contexts/LibraryContext';
interface AddToPlaylistMenuProps {
  track: Track;
}
export function AddToPlaylistMenu({
  track
}: AddToPlaylistMenuProps) {
  const {
    playlists,
    addToPlaylist,
    createPlaylist
  } = useLibrary();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [justAdded, setJustAdded] = useState<string | null>(null);
  const hostRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (hostRef.current && !hostRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  const add = (id: string) => {
    addToPlaylist(id, track);
    setJustAdded(id);
    window.setTimeout(() => setOpen(false), 450);
  };
  const createAndAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    createPlaylist(name, [track]);
    setName('');
    setOpen(false);
  };
  return <div ref={hostRef} className="relative" data-id="element-28" data-mp="379lv8ng5tig1">
      <button type="button" onClick={() => setOpen(o => !o)} aria-haspopup="menu" aria-expanded={open} aria-label={`Add ${track.title} to a playlist`} className="rounded-full p-1.5 text-muted transition-colors duration-150 ease-night hover:text-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-29" data-mp="37awxgityii51">
        <ListPlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-30" data-mp="wg98j3vcs2fz" />
      </button>

      <AnimatePresence data-id="element-31" data-mp="3pcarnt693u8y">
        {open && <motion.div role="menu" initial={{
        opacity: 0,
        scale: 0.96,
        y: -4
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.97,
        y: -2
      }} transition={{
        duration: 0.16,
        ease: [0.23, 1, 0.32, 1]
      }} className="absolute right-0 top-9 z-30 w-60 origin-top-right rounded-lg border border-ink-line bg-ink-panel p-2 shadow-2xl shadow-black/60" data-id="element-32" data-mp="3pbj5f8yjn8vc">
            <p className="px-2 pb-1.5 text-[11px] text-muted" data-id="element-33" data-mp="3bk4kgwl2sxln">Add to playlist</p>
            <ul className="max-h-44 overflow-y-auto scroll-thin" data-id="element-34" data-mp="3bi0bw1c8faf1">
              {playlists.length === 0 && <li className="px-2 py-1.5 text-xs text-muted" data-id="element-35" data-mp="3kcfzznenj0mu">No playlists yet — name one below.</li>}
              {playlists.map(playlist => {
            const has = playlist.tracks.some(t => t.id === track.id);
            return <li key={playlist.id} data-id="element-36" data-mp="2rukctpgq5vf7">
                    <button type="button" role="menuitem" onClick={() => add(playlist.id)} disabled={has} className="flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-sm text-cream transition-colors duration-150 ease-night hover:bg-ink-raised disabled:text-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-37" data-mp="2kde6phydtmct">
                      <span className="truncate" data-id="element-38" data-mp="1wmrxvogjzy1r">{playlist.name}</span>
                      {(has || justAdded === playlist.id) && <CheckIcon className="h-3.5 w-3.5 flex-none text-gold" aria-hidden="true" data-id="element-39" data-mp="1rp03mnsfrzue" />}
                    </button>
                  </li>;
          })}
            </ul>
            <form onSubmit={createAndAdd} className="mt-2 flex gap-1.5 border-t border-ink-line pt-2" data-id="element-40" data-mp="1iplhg0jaybfp">
              <input value={name} onChange={e => setName(e.target.value)} placeholder="New playlist…" aria-label="New playlist name" className="min-w-0 flex-1 rounded border border-ink-line bg-ink-base px-2 py-1.5 text-xs text-cream placeholder:text-muted focus:border-gold/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-41" data-mp="1dqim57t4jjkx" />
              <button type="submit" aria-label="Create playlist and add track" className="flex h-7 w-7 flex-none items-center justify-center rounded bg-gold text-ink-base transition-transform duration-150 ease-night hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-42" data-mp="2uzxsgxnvzgey">
                <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-43" data-mp="2q2xjoeznjzk5" />
              </button>
            </form>
          </motion.div>}
      </AnimatePresence>
    </div>;
}